import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEEKDAY_LABELS_FALLBACK = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

export default function AvailabilityTab({ token, t }) {
  const labels = t.admin;
  const weekdayLabels = labels.weekdays || WEEKDAY_LABELS_FALLBACK;

  const [config, setConfig] = useState({
    active_weekdays: [0, 1, 2, 3, 4, 5, 6],
    time_start: "11:00",
    time_end: "22:00",
    max_per_day: 1,
    terrace_address: "",
    arrival_instructions: "",
    what_to_bring: "",
  });
  const [blocked, setBlocked] = useState([]);
  const [newBlockedDate, setNewBlockedDate] = useState("");
  const [newBlockedReason, setNewBlockedReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [cfg, bl] = await Promise.all([
        axios.get(`${API}/availability/config`),
        axios.get(`${API}/availability/blocked-dates`),
      ]);
      setConfig(cfg.data);
      setBlocked(bl.data || []);
    } catch (err) {
      toast.error("Errore nel caricamento configurazione");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleWeekday = (wd) => {
    setConfig((c) => ({
      ...c,
      active_weekdays: c.active_weekdays.includes(wd)
        ? c.active_weekdays.filter((x) => x !== wd)
        : [...c.active_weekdays, wd].sort(),
    }));
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/availability/config`, config, {
        headers: { "X-Admin-Password": token },
      });
      toast.success(labels.configSaved || "Configurazione salvata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const addBlocked = async () => {
    if (!newBlockedDate) return;
    try {
      await axios.post(
        `${API}/availability/blocked-dates`,
        { date: newBlockedDate, reason: newBlockedReason },
        { headers: { "X-Admin-Password": token } }
      );
      setNewBlockedDate("");
      setNewBlockedReason("");
      const bl = await axios.get(`${API}/availability/blocked-dates`);
      setBlocked(bl.data || []);
      toast.success("Data bloccata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  const removeBlocked = async (date) => {
    try {
      await axios.delete(`${API}/availability/blocked-dates/${date}`, {
        headers: { "X-Admin-Password": token },
      });
      setBlocked((prev) => prev.filter((b) => b.date !== date));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  const inputCls = "bg-transparent border border-[#FAF9F6]/25 focus:border-[#C85A3C] outline-none px-3 py-2 text-[#FAF9F6] rounded-sm [color-scheme:dark]";

  return (
    <div data-testid="availability-tab">
      <h1 className="font-serif-display text-5xl mb-10">{labels.availability || "Disponibilità"}</h1>

      {/* Weekdays */}
      <section className="bg-[#FAF9F6]/5 border border-[#FAF9F6]/10 p-6 md:p-8 rounded-sm mb-8">
        <p className="overline text-[#C85A3C] mb-4">{labels.activeWeekdays || "Giorni attivi"}</p>
        <div className="flex flex-wrap gap-3">
          {weekdayLabels.map((label, wd) => {
            const active = config.active_weekdays.includes(wd);
            return (
              <button
                key={wd}
                data-testid={`weekday-${wd}`}
                onClick={() => toggleWeekday(wd)}
                className={`px-5 py-3 text-sm tracking-widest uppercase border rounded-sm transition ${
                  active
                    ? "bg-[#C85A3C] border-[#C85A3C] text-[#FAF9F6]"
                    : "border-[#FAF9F6]/25 text-[#FAF9F6]/60 hover:border-[#C85A3C]"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </section>

      {/* Time range + max per day */}
      <section className="bg-[#FAF9F6]/5 border border-[#FAF9F6]/10 p-6 md:p-8 rounded-sm mb-8 grid md:grid-cols-3 gap-6">
        <div>
          <p className="overline text-[#C85A3C] mb-2">{labels.timeRange || "Fascia oraria"}</p>
          <div className="flex items-center gap-3">
            <input
              type="time"
              data-testid="time-start"
              step="900"
              value={config.time_start}
              onChange={(e) => setConfig({ ...config, time_start: e.target.value })}
              className={inputCls}
            />
            <span className="text-[#FAF9F6]/40">—</span>
            <input
              type="time"
              data-testid="time-end"
              step="900"
              value={config.time_end}
              onChange={(e) => setConfig({ ...config, time_end: e.target.value })}
              className={inputCls}
            />
          </div>
        </div>
        <div>
          <p className="overline text-[#C85A3C] mb-2">{labels.maxPerDay || "Max eventi al giorno"}</p>
          <input
            type="number"
            data-testid="max-per-day"
            min={1}
            max={20}
            value={config.max_per_day}
            onChange={(e) => setConfig({ ...config, max_per_day: Number(e.target.value) })}
            className={inputCls + " w-24"}
          />
        </div>
        <div className="flex items-end">
          <button
            data-testid="save-availability"
            onClick={saveConfig}
            disabled={saving}
            className="btn-terracotta px-6 py-3 rounded-sm tracking-[0.2em] uppercase text-xs disabled:opacity-50"
          >
            {saving ? "…" : (labels.saveConfig || "Salva configurazione")}
          </button>
        </div>
      </section>

      {/* Reminder texts */}
      <section className="bg-[#FAF9F6]/5 border border-[#FAF9F6]/10 p-6 md:p-8 rounded-sm mb-8" data-testid="reminder-texts-section">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <p className="overline text-[#C85A3C]">Testi Promemoria</p>
            <p className="text-[11px] text-[#FAF9F6]/45 mt-1 leading-relaxed max-w-xl">
              Testi inclusi nelle email promemoria automatiche (48h e 24h prima dell&apos;esperienza). Solo ospiti con caparra pagata.
            </p>
          </div>
        </div>
        <div className="grid gap-5">
          <div>
            <label className="block text-[11px] tracking-widest uppercase text-[#FAF9F6]/60 mb-2">Indirizzo del terrazzo</label>
            <input
              type="text"
              data-testid="terrace-address-input"
              value={config.terrace_address || ""}
              onChange={(e) => setConfig({ ...config, terrace_address: e.target.value })}
              className={inputCls + " w-full"}
              placeholder="Via di Capo le Case, 18 · 00187 Roma"
            />
          </div>
          <div>
            <label className="block text-[11px] tracking-widest uppercase text-[#FAF9F6]/60 mb-2">Come arrivare</label>
            <textarea
              data-testid="arrival-instructions-input"
              rows={2}
              value={config.arrival_instructions || ""}
              onChange={(e) => setConfig({ ...config, arrival_instructions: e.target.value })}
              className={inputCls + " w-full resize-none"}
              placeholder="Suonare al citofono Pivano Fernando"
            />
          </div>
          <div>
            <label className="block text-[11px] tracking-widest uppercase text-[#FAF9F6]/60 mb-2">Cosa portare / cosa aspettarsi</label>
            <textarea
              data-testid="what-to-bring-input"
              rows={3}
              value={config.what_to_bring || ""}
              onChange={(e) => setConfig({ ...config, what_to_bring: e.target.value })}
              className={inputCls + " w-full resize-none"}
              placeholder="Vestiti comodi, vieni con appetito..."
            />
          </div>
          <div className="flex items-center gap-4 pt-1">
            <button
              data-testid="save-reminder-texts"
              onClick={saveConfig}
              disabled={saving}
              className="btn-terracotta px-6 py-2.5 rounded-sm tracking-[0.2em] uppercase text-xs disabled:opacity-50"
            >
              {saving ? "…" : "Salva testi"}
            </button>
            <span className="text-[11px] text-[#FAF9F6]/40">Automaticamente tradotto in EN/FR/ES/DE/JA dal template email</span>
          </div>
        </div>
      </section>

      {/* Blocked dates */}
      <section className="bg-[#FAF9F6]/5 border border-[#FAF9F6]/10 p-6 md:p-8 rounded-sm">
        <p className="overline text-[#C85A3C] mb-4">{labels.blockedDates || "Date bloccate"}</p>
        <div className="flex flex-wrap gap-3 items-center mb-6">
          <input
            type="date"
            data-testid="blocked-date-input"
            value={newBlockedDate}
            onChange={(e) => setNewBlockedDate(e.target.value)}
            min={new Date().toISOString().slice(0, 10)}
            className={inputCls}
          />
          <input
            type="text"
            data-testid="blocked-reason-input"
            placeholder={labels.blockedReason || "Motivo (opzionale)"}
            value={newBlockedReason}
            onChange={(e) => setNewBlockedReason(e.target.value)}
            className={inputCls + " flex-1 min-w-[220px]"}
          />
          <button
            data-testid="add-blocked-date"
            onClick={addBlocked}
            disabled={!newBlockedDate}
            className="btn-terracotta px-5 py-2.5 rounded-sm tracking-[0.2em] uppercase text-xs disabled:opacity-50"
          >
            {labels.addBlockedDate || "Aggiungi data"}
          </button>
        </div>

        {blocked.length === 0 ? (
          <p className="text-[#FAF9F6]/50 py-6" data-testid="no-blocked-dates">
            {labels.noBlockedDates || "Nessuna data bloccata."}
          </p>
        ) : (
          <ul className="divide-y divide-[#FAF9F6]/10" data-testid="blocked-dates-list">
            {blocked.map((b) => (
              <li
                key={b.date}
                data-testid={`blocked-date-${b.date}`}
                className="flex items-center justify-between py-3"
              >
                <div>
                  <div className="font-serif-display text-xl">
                    {format(parseISO(b.date), "d MMMM yyyy")}
                  </div>
                  {b.reason && (
                    <div className="text-[#FAF9F6]/55 text-sm mt-1">{b.reason}</div>
                  )}
                </div>
                <button
                  data-testid={`remove-blocked-${b.date}`}
                  onClick={() => removeBlocked(b.date)}
                  className="text-[#FAF9F6]/40 hover:text-[#C85A3C] transition p-2"
                  title={labels.blockedRemove || "Rimuovi"}
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
